import styled, { createGlobalStyle, keyframes, css} from 'styled-components';
import { MapContainer, TileLayer, Marker, GeoJSON, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import Select from 'react-select';
import L from 'leaflet';
import Cartographie from './components/Cartographie';
import { useState, useEffect } from 'react';

const GlobalStyle = createGlobalStyle`
  body {
    font-family: 'Montserrat', Arial, sans-serif;
    background: #fff;
    margin: 0;
    color: #232323;
  }
`;

const PageContainer = styled.div`
  width: 100%;
  max-width: 100vw;
  overflow-x: hidden;
`;

const LogoSection = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 48px 0;
  background: #fff;
`;

const Logo = styled.img`
  width: 180px;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    width: 140px;
  }
`;

const BrandTitle = styled.h1`
  font-size: 3.5rem;
  color: #A32A87;
  font-weight: 700;
  margin: 0;
  text-align: center;

  @media (max-width: 768px) {
    font-size: 2.8rem;
  }
`;

const Section = styled.section`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 64px 0;
  width: 100vw;
  background: ${props => props.alt ? '#faf6fa' : 'white'};

  @media (max-width: 768px) {
    flex-direction: column;
    padding: 32px 20px;
    text-align: center;
    width: 100%;
    box-sizing: border-box;
  }
`;

const SectionContent = styled.div`
  max-width: 600px;
  margin: 0 48px;

  @media (max-width: 768px) {
    margin: 0;
    order: 2;
    width: 100%;
  }
`;

const SectionTitle = styled.h2`
  font-size: 2.8rem;
  font-weight: 700;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    font-size: 2rem;
    margin-bottom: 16px;
  }
`;

const SectionText = styled.p`
  font-size: 1.3rem;
  line-height: 1.7;
  margin-bottom: 32px;

  @media (max-width: 768px) {
    font-size: 1.1rem;
    margin-bottom: 24px;
  }
`;

const SectionImage = styled.div`
  min-width: 350px;
  min-height: 250px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 48px;
  font-size: 1.2rem;
  color: #A32A87;
  transition: transform 0.3s ease-out;

  @media (max-width: 768px) {
    min-width: unset;
    width: 100%;
    margin: 0 0 24px 0;
    order: 1;

    img {
      width: 100%;
      max-width: 300px;
      height: auto;
      display: block;
      margin: 0 auto;
    }
  }
`;

const DemoSection = styled(Section)`
  background: #faf6fa;
  flex-direction: column;
  text-align: center;
`;

const DemoContainer = styled.div`
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
  aspect-ratio: 16/9;
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(0,0,0,0.1);

  iframe {
    width: 100%;
    height: 100%;
  }
`;

const FounderSection = styled.section`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 64px 0 32px 0;
  width: 100vw;

  @media (max-width: 768px) {
    flex-direction: column;
    padding: 32px 20px;
    text-align: center;
    width: 100%;
    box-sizing: border-box;
  }
`;

const FounderPhoto = styled.img`
  width: 160px;
  height: 160px;
  border-radius: 50%;
  object-fit: cover;
  margin: 0 48px;

  @media (max-width: 768px) {
    margin: 0 0 24px 0;
  }
`;

const FounderContent = styled.div`
  max-width: 600px;

  @media (max-width: 768px) {
    margin-bottom: 24px;
  }
`;

const FounderName = styled.h3`
  font-size: 2rem;
  font-weight: 700;
  margin: 0;
`;

const FounderTitle = styled.p`
  font-size: 1.1rem;
  color: #666;
  margin: 0 0 16px 0;
`;

const FounderText = styled.p`
  font-size: 1.1rem;
  line-height: 1.6;
`;

const Partners = styled.div`
  display: flex;
  align-items: center;
  margin-left: 48px;
  gap: 32px;

  @media (max-width: 768px) {
    margin-left: 0;
    justify-content: center;
    flex-wrap: wrap;
    width: 100%;
    gap: 20px;
  }
`;

const PartnerLogo = styled.img`
  height: 88px;

  @media (max-width: 768px) {
    height: 60px;
  }
`;

const Intro = styled.p`
  text-align: center;
  max-width: 700px;
  margin: 0 auto 48px auto;
  font-size: 1.25rem;
  color: #444;
  padding: 0 20px;

  @media (max-width: 768px) {
    font-size: 1.1rem;
    padding: 0 20px;
    width: 100%;
    box-sizing: border-box;
  }
`;

const CartographieSection = styled(Section)`
  flex-direction: column;
  padding: 64px 32px;
  background: #fff;
`;

const CartographieLayout = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: flex-start;
  width: 100%;
  min-height: 80vh;
  box-sizing: border-box;
  @media (max-width: 1200px) {
    flex-direction: column;
    align-items: center;
    min-height: auto;
    width: 100%;
    padding: 0 10px;
  }
`;

const CartographieSidebar = styled.div`
  min-width: 320px;
  max-width: 500px;
  background: #faf6fa;
  border-radius: 24px;
  box-shadow: 0 2px 16px #0001;
  padding: 32px 24px 16px 24px;
  margin: 0 32px 0 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 18px;
  height: 75vh;
  @media (max-width: 1200px) {
    width: 100%;
    margin: 0 auto 24px auto;
    min-width: unset;
    max-width: unset;
    height: auto;
    padding: 24px 16px;
    box-sizing: border-box;
  }
`;

const CartographieMapArea = styled.div`
  flex: 1;
  display: flex;
  align-items: stretch;
  justify-content: center;
  position: relative;
  max-width: 700px;
  min-width: 400px;
  margin-right: 0;
  background: #fff;
  border-radius: 24px;
  box-shadow: 0 2px 16px #0001;
  @media (max-width: 1200px) {
    width: 100%;
    min-width: unset;
    max-width: unset;
    margin: 0 auto;
    box-sizing: border-box;
  }
`;

const StyledMapContainer = styled(MapContainer)`
  width: 100%;
  height: 75vh;
  border-radius: 24px;
  z-index: 1;
  @media (max-width: 1200px) {
    height: 60vh;
  }
`;

const ContactSection = styled.section`
  padding: 64px 32px;
  text-align: center;
  background: #faf6fa;
  color: #232323;
  font-size: 1.2rem;
  line-height: 1.6;

  @media (max-width: 768px) {
    padding: 32px 20px;
    font-size: 1rem;
  }
`;

const ContactInfo = styled.p`
  margin: 8px 0;
`;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.9);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const BubbleSection = styled(Section)`
  background: white;
  padding: 80px 0;
`;

const BubbleContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 32px;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  flex-wrap: wrap; /* Permet le passage à la ligne sur mobile */

  @media (max-width: 1024px) {
    flex-direction: column; /* Empile les bulles sur les écrans plus petits */
    align-items: center;
    gap: 24px;
  }
`;

const Bubble = styled.div`
  flex: 1; /* Permet aux bulles de prendre l'espace disponible */
  min-width: 250px; /* Largeur minimale pour éviter un texte trop étroit */
  max-width: 280px; /* Largeur maximale pour maintenir une taille raisonnable */
  background: linear-gradient(45deg, #A32A87, #d31eff);
  color: white;
  border-radius: 16px;
  padding: 24px;
  text-align: center;
  box-shadow: 0 8px 16px rgba(0,0,0,0.2);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  animation: ${fadeInUp} 0.6s ease-out forwards; /* Animation d'apparition */

  @media (max-width: 1024px) {
    width: 90%; /* Sur mobile, prend 90% de la largeur du conteneur */
    max-width: 400px; /* Limite la largeur même sur mobile */
  }
`;

const BubbleTitle = styled.h3`
  font-size: 1.4rem;
  font-weight: 700;
  margin: 0 0 12px 0;
`;

const BubbleText = styled.p`
  font-size: 1rem;
  line-height: 1.5;
  margin: 0;
`;

const TimeSection = styled.section`
  padding: 80px 32px;
  text-align: center;
  background: #fff;
  
  @media (max-width: 768px) {
    padding: 48px 20px;
  }
`;

const TimeContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const TimePhrase = styled.h2`
  font-size: 2.2rem;
  color: #232323;
  font-weight: 400;
  margin-bottom: 24px;
  
  @media (max-width: 768px) {
    font-size: 1.6rem;
  }
`;

const TimeSaving = styled.h3`
  font-size: 2.8rem;
  color: #A32A87;
  font-weight: 700;
  margin: 0;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const numberAnimation = keyframes`
  0% { transform: translateY(0px); }
  25% { transform: translateY(-5px); }
  50% { transform: translateY(0px); }
  75% { transform: translateY(5px); }
  100% { transform: translateY(0px); }
`;

const glowPulse = keyframes`
  0% { text-shadow: 0 0 5px rgba(163, 42, 135, 0.3); }
  50% { text-shadow: 0 0 20px rgba(163, 42, 135, 0.8), 0 0 30px rgba(163, 42, 135, 0.5); }
  100% { text-shadow: 0 0 5px rgba(163, 42, 135, 0.3); }
`;

const AnimatedNumberContainer = styled.span`
  display: inline-block;
  font-weight: 700;
  color: ${props => props.highlight ? '#A32A87' : '#232323'};
  font-size: 1.2em;
  margin: 0 8px;
  min-width: 20px;

  ${props => props.highlight && css`
    animation: ${numberAnimation} 2s ease-in-out infinite,
               ${glowPulse} 2s ease-in-out infinite;
  `}
`;

// Composant pour les nombres animés
function AnimatedNumber({ number, highlight }) {
   return <AnimatedNumberContainer highlight={highlight}>{number}</AnimatedNumberContainer>;
}

function App() {
  const [daysSaved, setDaysSaved] = useState(3);

  useEffect(() => {
    const interval = setInterval(() => {
      // génère un entier aléatoire entre 2 et 5 inclus
      const min = 2;
      const max = 5;
      const randomDays = Math.floor(Math.random() * (max - min + 1)) + min;
      setDaysSaved(randomDays);
    }, 1000);
    
    return () => clearInterval(interval);
  }, []);

  return (
    <>
      <GlobalStyle />
      <PageContainer>
        <LogoSection>
          <Logo src={process.env.PUBLIC_URL + '/logo192.png'} alt="Logo Domicare" />
          <BrandTitle>DOMICARE</BrandTitle>
        </LogoSection>

        <Intro>
          Domicare s'intègre à vos outils pour générer automatiquement des plannings mensuels optimisés, en quelques minutes.
        </Intro>

        <Section>
          <SectionContent>
            <SectionTitle>Création automatique de plannings mensuels</SectionTitle>
            <SectionText>
            Grâce a un modèle d'intelligence adaptative, Domicare analyse vos données, identifie les cycles de planification existants et automatise l'organisation de vos prestations pour une gestion optimale.
            </SectionText>
          </SectionContent>
          <SectionImage>
            <img src={process.env.PUBLIC_URL + '/pngordinateur.png'} alt="Interface Domicare" style={{ width: '400px', height: '400px', objectFit: 'contain' }} />
          </SectionImage>
        </Section>

        <Section alt>
          <SectionImage>
            <img src={process.env.PUBLIC_URL + '/para2.png'} alt="Illustration aide à domicile" style={{ width: '400px', height: '400px', objectFit: 'contain' }} />
          </SectionImage>
          <SectionContent>
            <SectionTitle>
              Libérez votre temps pour l'humain
            </SectionTitle>
            <SectionText>
              Fini les heures perdues à réajuster chaque mois les plannings : concentrez-vous sur ce qui compte vraiment. Nous vous libérons du temps pour recruter, former et accompagner vos équipes, tout en offrant un meilleur suivi aux bénéficiaires.
            </SectionText>
          </SectionContent>
        </Section>




        <BubbleSection>
          <BubbleContainer>
            <Bubble>
              <BubbleTitle>15 minutes</BubbleTitle>
              <BubbleText>On récupère les informations, vous n’avez presque rien à saisir. Planning prêt en 15 min.</BubbleText>
            </Bubble>
            <Bubble>
              <BubbleTitle>Conformité RH</BubbleTitle>
              <BubbleText>Nous générons des plannings stables qui respectent les règles du droit du travail et les contraintes métier.</BubbleText>
            </Bubble>
            <Bubble>
              <BubbleTitle>Gains €</BubbleTitle>
              <BubbleText>Les planning que nous fournissons vous permettent d'optimiser les temps de trajet, les temps d'inactivité et la modulation.</BubbleText>
            </Bubble>
          </BubbleContainer>
        </BubbleSection>

        <TimeSection>
          <TimeContainer>
            <TimePhrase>
              Vous passez <AnimatedNumber number={daysSaved} /> jours à créer vos plannings en fin de mois ?
            </TimePhrase>
            <TimeSaving>
              Gagnez <AnimatedNumber number={daysSaved} highlight /> jours avec Domicare chaque mois !
            </TimeSaving>
          </TimeContainer>
        </TimeSection>



        <DemoSection>
          <SectionTitle>Démonstration</SectionTitle>
          <SectionText>
            Découvrez comment Domicare transforme la gestion de vos plannings en quelques minutes.
          </SectionText>
          <DemoContainer>
            <iframe src="https://www.youtube.com/embed/Y_ItWo4pfSY" title="Démonstration Domicare" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen></iframe>
          </DemoContainer>
        </DemoSection>


        <CartographieSection>
          <Cartographie />
        </CartographieSection>

        <ContactSection>
          <ContactInfo>Email : hugo.peeters@domicare.fr</ContactInfo>
          <ContactInfo>Téléphone : +33 6 74 83 93 29</ContactInfo>
        </ContactSection>
      </PageContainer>
    </>
  );
}

export default App;
